/**
 * Manages pagination functionality for the PS Table component.
 *
 * PSTablePaging handles page navigation, page size selection, and provides
 * pagination summary information. It supports both client-side and server-side
 * pagination with configurable page sizes and navigation controls.
 *
 * @class PSTablePaging
 * @example
 * // Create pagination with default settings
 * const paging = new PSTablePaging();
 *
 * // Create pagination with custom options
 * const paging = new PSTablePaging({
 *   enabled: true,
 *   page_size: 25,
 *   page_size_options: [10, 25, 50, 100],
 *   offset: 0
 * });
 *
 * // Disable pagination completely
 * const paging = new PSTablePaging(false);
 */
class PSTablePaging {
	/**
	 * Creates a new PSTablePaging instance.
	 *
	 * @param {Object|boolean} options - Pagination configuration options
	 * @param {boolean} [options.enabled=true] - Whether the pagination UI is enabled
	 * @param {number} [options.offset=0] - The current offset for server-side pagination. This is used by various data sources to determine the starting point for the current page.
	 * @param {number} [options.page_size=10] - The number of records per page
	 * @param {number} [options.limit] - Alternative to page_size for backward compatibility
	 * @param {Array<number>} [options.page_size_options=[10, 20, 50, 100]] - Available page size options
	 */
	constructor(options) {
		// Handle boolean input by converting to object with enabled property
		if (options === false)
			options = { enabled: false };

		options = options ?? {};

		this.enabled = ko_helper.safe_observable(options.enabled, true);
		this.offset = ko_helper.safe_observable(options.offset, 0);
		this.page_size = ko_helper.safe_observable(options.page_size ?? options.limit, this.enabled() ? 10 : 10000);
		this.page_size_options = ko_helper.safe_observableArray(options.page_size_options, [10, 20, 50, 100]);

		/**
		 * The total number of records available across all pages.
		 * @type {ko.Observable<number>}
		 */
		this.total_records = ko.observable(0);

		/**
		 * The current page number (1-based).
		 * @type {ko.Observable<number>}
		 */
		this.page_number = ko.observable(1);

		/**
		 * Computed observable that calculates the total number of pages.
		 * @type {ko.PureComputed<number>}
		 */
		this.total_pages = ko.pureComputed(() => {
			return Math.ceil(this.total_records() / this.page_size());
		});

		/**
		 * Function to change the current page number.
		 *
		 * @param {number} page_number - The new page number to navigate to
		 */
		this.change_page = (page_number) => {
			this.page_number(page_number);
		};

		/**
		 * Computed observable that provides a human-readable pagination summary.
		 * Shows the current range of records being displayed.
		 * @type {ko.PureComputed<string>}
		 */
		this.pagination_summary = ko.pureComputed(() => {
			let total = this.total_records();
			let start = this.offset() + 1;
			let end = Math.min(this.offset() + this.page_size(), total);

			return `Showing ${start} - ${end} of ${total}`;
		});
	}
}

export default PSTablePaging;
